import {IFA_ONLY_RATING} from "../components/product/ProductMapper";
import {IPortfolioFeesTreeNode, PEER_UNIVERSE_MEDIAN} from "../api/PortfolioApi";

export enum AccountType {
    ATOMIC = 1,
    COMPOSITE = 2,
    BENCHMARK = 3
}

export interface ITreeNodeBase {
    id: number,
    name: string,
    sortOrder: number,
    accountType: AccountType,
    date: string,
    backstopId?: string,
    parentId?: number | null,
}

export interface IPortfolioPerformance {
    quarter: number | null,
    ytd: number | null,
    year: number | null,
    threeYears: number | null,
    fiveYears: number | null,
    tenYears: number | null
}

export interface IPlanTreeNode extends ITreeNodeBase {
    marketValue: number,
    marketValuePctOfTotal: number,
    marketValuePctOfParent: number,
    relativePerformance: IPortfolioPerformance,
    absolutePerformance: IPortfolioPerformance,
    priorMarketValuePctOfTotal?: number | undefined;
    managementType?: string | undefined;
    benchmarkId?: number | undefined;
    rating?: string | undefined;
    ifaRating?: string | undefined;
}

export interface IPlanTreeTargetAllocation {
    accountName: string,
    marketValuePctOfTotal: number,
    targetAllocation: number,
    asOfDate: string;
}

export interface IPlanTreeQuarterlyPerformance {
    date: Date,
    quarterAbsolute?: number | null,
    quarterRelative?: number | null,
    benchmarkName?: string,
}

export interface IAssetAllocationTreeNode extends ITreeNodeBase {
    nodes?: IAssetAllocationTreeNode[] | null;
    marketValue: number,
    marketValuePctOfTotal: number,
    marketValuePctOfParent: number,
    rating: string,
    priorMarketValuePctOfTotal?: number;
}

export interface IPerformanceTreeNode extends ITreeNodeBase {
    nodes?: IPerformanceTreeNode[] | null;
    relativePerformance: IPortfolioPerformance;
    absolutePerformance: IPortfolioPerformance;
    managementType?: string | undefined;
}

export interface IBenchmarkReturn {
    benchmarkId: number;
    name: string;
    date: string;
    performance: IPortfolioPerformance;
}

export interface IFeesTreeNode extends IPortfolioFeesTreeNode {
    nodes: IFeesTreeNode[] | null;
}

export const DEFAULT_RELATIVE_PERFORMANCE: IPortfolioPerformance = {
    quarter: -0.25,
    ytd: null,
    year: 0.0,
    threeYears: -0.99,
    fiveYears: -0.5,
    tenYears: -10.0
};

export const DEFAULT_ABSOLUTE_PERFORMANCE: IPortfolioPerformance = {
    quarter: null,
    ytd: 0,
    year: 3.6,
    threeYears: 3.8,
    fiveYears: 4.0,
    tenYears: 4.5
};

export const DEFAULT_NODE_DEC_2000: IPlanTreeNode = {
    id: -1,
    name: "default",
    sortOrder: 1,
    accountType: AccountType.COMPOSITE,
    marketValue: 100000,
    marketValuePctOfTotal: 10,
    marketValuePctOfParent: 100,
    parentId: 0,
    date: "2000-12-31",
    relativePerformance: DEFAULT_RELATIVE_PERFORMANCE,
    absolutePerformance: DEFAULT_ABSOLUTE_PERFORMANCE,
    managementType: undefined
};

export const DEFAULT_NODE_SEP_2000: IPlanTreeNode = {...DEFAULT_NODE_DEC_2000, date: "2000-09-30"};
export const DEFAULT_NODE_JUN_2000: IPlanTreeNode = {...DEFAULT_NODE_DEC_2000, date: "2000-06-30"};
export const BENCHMARK_1_RETURN_DEC: IBenchmarkReturn = {
    benchmarkId: 10,
    name: "Benchmark1",
    date: "2000-12-31",
    performance: DEFAULT_ABSOLUTE_PERFORMANCE
};

export const BENCHMARK_2_RETURN_DEC = {
    ...BENCHMARK_1_RETURN_DEC,
    benchmarkId: 20, name: "Benchmark2"
};

export const PLAN_TREE_DEC_2000_FOR_TEST: IPlanTreeNode[] = [
    {
        ...DEFAULT_NODE_DEC_2000,
        id: 221,
        name: "GrandChild221",
        sortOrder: 221,
        accountType: AccountType.ATOMIC,
        parentId: 22,
        managementType: undefined,
        benchmarkId: 10,
    },
    {
        ...DEFAULT_NODE_DEC_2000,
        id: 22,
        name: "Child22",
        sortOrder: 22,
        accountType: AccountType.COMPOSITE,
        parentId: 2,
    },
    {
        ...DEFAULT_NODE_DEC_2000,
        id: 21,
        name: "Child21",
        sortOrder: 21,
        accountType: AccountType.ATOMIC,
        parentId: 2,
        backstopId: "21",
        benchmarkId: 20,
    },
    {
        ...DEFAULT_NODE_DEC_2000,
        id: 12,
        name: "Child12",
        sortOrder: 12,
        accountType: AccountType.ATOMIC,
        parentId: 1,
        managementType: "otherThanPassive"
    },
    {
        ...DEFAULT_NODE_DEC_2000,
        id: 11,
        name: "Child11",
        sortOrder: 11,
        accountType: AccountType.ATOMIC,
        parentId: 1,
        managementType: "Passive",
    },
    {
        ...DEFAULT_NODE_DEC_2000,
        id: 2,
        name: "Parent2",
        sortOrder: 2,
        marketValue: 3456.89,
        marketValuePctOfTotal: 10.52765,
        marketValuePctOfParent: 100.126,
    },
    {
        ...DEFAULT_NODE_DEC_2000,
        id: 1,
        name: "Parent1",
        sortOrder: 1,
        marketValuePctOfTotal: 33.632,
    },
    {
        ...DEFAULT_NODE_DEC_2000,
        id: 0,
        name: "Total",
        sortOrder: 0,
        parentId: null,
        marketValue: 345678.7,
        marketValuePctOfTotal: 100,
        marketValuePctOfParent: 100,
        benchmarkId: 10
    },
    {
        ...DEFAULT_NODE_DEC_2000,
        id: 3,
        name: "Parent3",
        sortOrder: 3,
        backstopId: "123",
        rating: IFA_ONLY_RATING,
        ifaRating: "Not Recommended"
    },
    {
        ...DEFAULT_NODE_DEC_2000,
        id: 4,
        name: "Parent4",
        sortOrder: 4,
        backstopId: "789",
        rating: "Buy",
    },
    {
        ...DEFAULT_NODE_DEC_2000,
        id: 5,
        name: "Parent5",
        sortOrder: 5
    }
];

export const PLAN_TREE_DEC_2000_FOR_TEST_WITH_RATING: IPlanTreeNode[] = [
    ...PLAN_TREE_DEC_2000_FOR_TEST.map(node => ({...node,
        rating: node.id === 3 ? "Sell" : "",
        priorMarketValuePctOfTotal:
            node.id === 5 ? 3.48 :
            node.id === 4 ? -3.43 :
            node.id === 3 ? 0.0 :
            undefined
    })),
];

export const PLAN_TREE_SEP_2000_FOR_TEST: IPlanTreeNode[] = [
    {
        ...DEFAULT_NODE_SEP_2000,
        id: 221,
        name: "GrandChild221",
        sortOrder: 221,
        accountType: AccountType.ATOMIC,
        parentId: 22,
    },
    {
        ...DEFAULT_NODE_SEP_2000,
        id: 22,
        name: "Child22",
        sortOrder: 22,
        accountType: AccountType.COMPOSITE,
        parentId: 2,
    },
    {
        ...DEFAULT_NODE_SEP_2000,
        id: 21,
        name: "Child21",
        sortOrder: 21,
        accountType: AccountType.ATOMIC,
        parentId: 2,
        backstopId: "21",
    },
    {
        ...DEFAULT_NODE_SEP_2000,
        id: 12,
        name: "Child12",
        sortOrder: 12,
        accountType: AccountType.ATOMIC,
        parentId: 1,
    },
    {
        ...DEFAULT_NODE_SEP_2000,
        id: 11,
        name: "Child11",
        sortOrder: 11,
        accountType: AccountType.ATOMIC,
        parentId: 1,
    },
    {
        ...DEFAULT_NODE_SEP_2000,
        id: 2,
        name: "Parent2 Sep",
        sortOrder: 2,
        marketValuePctOfTotal: 15.682,
    },
    {
        ...DEFAULT_NODE_SEP_2000,
        id: 1,
        name: "Parent1 Sep",
        sortOrder: 1,
        marketValuePctOfTotal: 30.879,
    },
    {
        ...DEFAULT_NODE_SEP_2000,
        id: 0,
        name: "Total",
        sortOrder: 0,
        parentId: null,
        marketValue: 945678.4,
        marketValuePctOfTotal: 100,
        marketValuePctOfParent: 100,
    },
    {
        ...DEFAULT_NODE_SEP_2000,
        id: 4,
        name: "Parent4 Sep",
        sortOrder: 4,
        backstopId: "789",
    }
];

export const PLAN_TREE_FOR_TEST: IPlanTreeNode[] = [
    ...PLAN_TREE_DEC_2000_FOR_TEST,
    ...PLAN_TREE_SEP_2000_FOR_TEST,
];

export const PLAN_TREE_JUN_2000_FOR_TEST: IPlanTreeNode[] = [
    {
        ...DEFAULT_NODE_JUN_2000,
        id: 2,
        name: "Parent2 Jun",
        sortOrder: 2,
        marketValuePctOfTotal: 18.482,
    },
    {
        ...DEFAULT_NODE_JUN_2000,
        id: 1,
        name: "Parent1 Jun",
        sortOrder: 1,
        marketValuePctOfTotal: 37.737,
    },
];

export const PLAN_TREE_MULTI_PRIOR_PERIOD_FOR_TEST: IPlanTreeNode[] = [
    ...PLAN_TREE_DEC_2000_FOR_TEST,
    ...PLAN_TREE_SEP_2000_FOR_TEST,
    ...PLAN_TREE_JUN_2000_FOR_TEST,
];

export const DEFAULT_FEE_TREE_NODE: IPortfolioFeesTreeNode = {
    id: 1,
    date: "2000-12-31",
    name: "Parent",
    parentId: null,
    sortOrder: 1,
    accountType: AccountType.COMPOSITE,
    backstopId: undefined,
    peerUniverseName: null,
    netExpenseRatio: null,
    revenueShare: null,
    effectiveNetExpenseRatio: null,
    peerUniverseMedian: null,
    percentile25: null,
    percentile50: null,
    percentile75: null,
    population: null,
};

export const PORTFOLIO_FEES_TREE_FOR_TEST: IPortfolioFeesTreeNode[] = [
    DEFAULT_FEE_TREE_NODE,
    {...DEFAULT_FEE_TREE_NODE,
        id: 3,
        name: "Parent1",
        parentId: 1,
        sortOrder: 2,
    },
    {...DEFAULT_FEE_TREE_NODE,
        id: 11,
        name: "Child11",
        parentId: 3,
        accountType: AccountType.ATOMIC,
        sortOrder: 5,
        backstopId: "11",
        peerUniverseName: "Child11-peer",
        netExpenseRatio: 0.12,
        revenueShare: 0,
        effectiveNetExpenseRatio: 0.43,
        peerUniverseMedian: PEER_UNIVERSE_MEDIAN.ABOVE_MEDIAN,
        percentile25: 0.2,
        percentile50: 0.35,
        percentile75: 0.452,
        population: 1234

    },
    {...DEFAULT_FEE_TREE_NODE,
        id: 12,
        name: "Child12",
        parentId: 3,
        accountType: AccountType.ATOMIC,
        sortOrder: 9,
        peerUniverseName: "Child12-peer",
        netExpenseRatio: 0.3,
        revenueShare: null,
        effectiveNetExpenseRatio: 0.345,
        peerUniverseMedian: PEER_UNIVERSE_MEDIAN.BELOW_MEDIAN,
        percentile50: 0.445,
        percentile75: 0.545

    },
    {...DEFAULT_FEE_TREE_NODE,
        id: 13,
        name: "Child13",
        parentId: 3,
        accountType: AccountType.ATOMIC,
        sortOrder: 11,
        peerUniverseName: "Child13-peer",
        netExpenseRatio: 0.3,
        revenueShare: null,
        effectiveNetExpenseRatio: 0.445,
        peerUniverseMedian: PEER_UNIVERSE_MEDIAN.AT_MEDIAN,
        percentile25: 0.245,
        percentile50: 0.445,

    },
    {...DEFAULT_FEE_TREE_NODE,
        id: 14,
        name: "Child14",
        parentId: 3,
        accountType: AccountType.ATOMIC,
        sortOrder: 22,
        peerUniverseName: "Child14-peer",
        netExpenseRatio: 0.3,
        revenueShare: null,
        effectiveNetExpenseRatio: null,
        peerUniverseMedian: PEER_UNIVERSE_MEDIAN.UNKNOWN

    }
];